/**
 * Renders a chronological list of pipeline step events for a prospect (labels, status, metadata).
 *
 * Where: `PipelinePreviewCard` and anywhere pipeline history is shown on a lead.
 * Uses: MUI chips/typography only; no API calls.
 * Behavior: memoized sort; empty copy when no entries.
 */
import React, { useMemo } from 'react';
import { Box, Typography, Chip, Stack, Divider } from '@mui/material';

export interface ProspectPipelineTimelineEntry {
  timestamp?: string;
  kind?: string;
  status?: string | null;
  message?: string | null;
  step?: {
    uuid?: string;
    name?: string;
    type?: string;
    action?: string;
    event?: string;
  } | null;
  metadata?: Record<string, unknown> | null;
}

export interface ProspectPipelineTimelineProps {
  timeline?: ProspectPipelineTimelineEntry[] | null;
}

export const ProspectPipelineTimeline: React.FC<ProspectPipelineTimelineProps> = ({ timeline }) => {
  const items = useMemo(() => (Array.isArray(timeline) ? timeline : []), [timeline]);

  if (!items.length) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          No activity recorded yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Activity timeline
      </Typography>
      <Stack spacing={0.75}>
        {items.map((entry, index) => {
          const ts = entry.timestamp ? new Date(entry.timestamp) : null;
          const isLast = index === items.length - 1;

          return (
            <Box key={`${entry.timestamp || index}-${entry.status || ''}`}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 90 }}>
                  <Typography variant="caption" color="text.secondary">
                    {ts ? ts.toLocaleDateString() : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {ts ? ts.toLocaleTimeString() : ''}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
                    {entry.kind && (
                      <Chip
                        size="small"
                        label={entry.kind}
                        variant="outlined"
                        color="default"
                      />
                    )}
                    {entry.status && (
                      <Chip
                        size="small"
                        label={entry.status}
                        color={
                          entry.status === 'failed'
                            ? 'error'
                            : entry.status === 'success'
                            ? 'success'
                            : entry.status === 'retry_later'
                            ? 'warning'
                            : 'default'
                        }
                        variant="outlined"
                      />
                    )}
                    {entry.step?.name && (
                      <Chip
                        size="small"
                        label={entry.step.name}
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </Box>
                  {entry.message && (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {entry.message}
                    </Typography>
                  )}
                </Box>
              </Box>
              {!isLast && (
                <Divider
                  sx={{
                    mt: 0.5,
                    mb: 0.5,
                    opacity: 0.4,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};


